import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useCallback, useState, useMemo } from "react";
import purifyMarked from "@ploy-lib/purify-marked";
import { getIn } from "formik";
import clsx from "clsx";
import { defineMessages, FormattedMessage } from "react-intl";
import { Modal, Paper, makeStyles } from "@material-ui/core";
import { Icon, Box, Button, FormHelperText } from "@material-ui/core";
import Send from "@material-ui/icons/Send";
import NavigateBefore from "@material-ui/icons/NavigateBefore";
import NavigateNext from "@material-ui/icons/NavigateNext";
import MoreVert from "@material-ui/icons/MoreVert";
import { download } from "@ploy-lib/core";
import { PendingButton } from "@ploy-ui/core";
import { LoadingOverlay } from "./LoadingOverlay";
import { identityRecordOfFieldEditorOptions, DployFormControl, getFieldError } from "@ploy-ui/form-fields";
import { usePageState } from "../../PageContext";
import { useNavigationState } from "../../NavigationContext";
import { useTemplateFieldIsVisible, useTemplateSectionIsVisible, useFormHasErrors } from "../../hooks";
import { AppActions } from "../../components/AppActions";
import { touchAllVisibleFields } from "../../components/TouchVisibleFieldsOnPreviousPages";
import { usePages } from "../../pagesContext";
import { isButtonColor, isButtonVariant, commonButtonColors, commonButtonVariants } from "./ButtonCommon";
import { LiteralBaseInternal } from "../literals/TextLiteral";
import { useDispatch } from "@ploy-lib/calculation";
import icons from "./Icons";
import { useGTMExtended } from "../../thirdPartyScripts/GoogleTagManagerHandler";
const buttonRoleLocalization = defineMessages({
    navigate_before: {
        id: "buttonRoleScope.navigate_before",
        defaultMessage: "Navigate to previous"
    },
    navigate_next: {
        id: "buttonRoleScope.navigate_next",
        defaultMessage: "Navigate to next"
    },
    submit: {
        id: "buttonRoleScope.submit",
        defaultMessage: "Submit"
    },
    submit_no_validate: {
        id: "buttonRoleScope.submit_no_validate",
        defaultMessage: "Submit, no validation"
    },
    navigate_home: {
        id: "buttonRoleScope.navigate_home",
        defaultMessage: "Navigate to home"
    },
    validateForm: {
        id: "buttonRoleScope.validateForm",
        defaultMessage: "Validate form"
    }
});
const useStyles = makeStyles((theme) => ({
    modal: {
        position: "absolute",
        width: "100%",
        backgroundColor: "rgba(0,0,0,0.5)",
        padding: theme.spacing(3),
        display: "flex",
        overflowY: "auto"
    },
    modalContainer: {
        margin: "auto"
    },
    modalContent: { padding: 30, width: 350 },
    modalConfirmButton: {
        marginTop: 10,
        width: "100%",
        backgroundColor: "lightgray"
    },
    buttonAsText: {
        margin: 0
    },
    mailtoButton: ({ textAlign }) => ({
        textAlign: textAlign
    })
}));
function ButtonFieldImpl({ className, label, color, variant, buttonRole: role, icon, form, field, onClick, fullWidth, disabled, pending, success, margin, startIcon, saveOnClick, endIcon, showOverlay, errorDisplay, options = {}, buttonSize, muiStartIcon, muiEndIcon }) {
    const { touched, setTouched, errors, setErrors, setFieldValue, submitForm, setSubmitting } = form;
    const pushGTMEvent = useGTMExtended();
    const dispatch = useDispatch();
    const buttonColor = isButtonColor(color) ? color : undefined;
    const buttonVariant = isButtonVariant(variant) ? variant : undefined;
    const { style } = options;
    const pages = usePages();
    const { next, prev, step, isLastStep, getStepHref } = usePageState();
    const formHasErrors = useFormHasErrors();
    const hasFormErrors = useMemo(() => formHasErrors({ errors, touched }, undefined, true), [formHasErrors, errors, touched]);
    const errorProps = useMemo(() => getFieldError(errors, touched, field.name, errorDisplay), [errorDisplay, errors, field.name, touched]);
    const hasPageErrors = useMemo(() => formHasErrors({ errors, touched }, pages.slice(0, step + 1), false), [formHasErrors, errors, touched, pages, step]);
    const [appActionsAnchor, setAppActionsAnchor] = useState();
    const fieldIsVisible = useTemplateFieldIsVisible();
    const sectionIsVisible = useTemplateSectionIsVisible();
    const navigateNext = useCallback(() => {
        const relevantTouched = touchAllVisibleFields(touched, pages.slice(0, step + 1), fieldIsVisible, sectionIsVisible);
        setTouched(relevantTouched);
        if (!formHasErrors({ errors, touched: relevantTouched }, pages.slice(0, step + 1), false)) {
            if (saveOnClick)
                dispatch({
                    type: "add_changes",
                    meta: {
                        changes: [{ changedFieldId: "Save_on_next", namespace: "Main" }]
                    }
                });
            next();
        }
    }, [
        dispatch,
        errors,
        fieldIsVisible,
        formHasErrors,
        next,
        pages,
        saveOnClick,
        sectionIsVisible,
        setTouched,
        step,
        touched
    ]);
    const validateForm = useCallback(async (e) => {
        const allTouched = touchAllVisibleFields(touched, pages, fieldIsVisible, sectionIsVisible);
        setTouched(allTouched);
        if (formHasErrors({ errors, touched: allTouched }, undefined, true)) {
            throw new Error("Failed to validate");
        }
        if (onClick)
            return onClick(e);
    }, [
        errors,
        fieldIsVisible,
        formHasErrors,
        onClick,
        pages,
        sectionIsVisible,
        setTouched,
        touched
    ]);
    const submit = useCallback(async (shouldValidate, e) => {
        setSubmitting(true);
        const allTouched = touchAllVisibleFields(touched, pages, fieldIsVisible, sectionIsVisible, true);
        if (shouldValidate) {
            setTouched(allTouched);
            if (formHasErrors({ errors, touched: allTouched }, undefined, true)) {
                setSubmitting(false);
                throw new Error("Failed to validate");
            }
        }
        const result = onClick
            ? (await onClick(e))
            : null;
        if (onClick && result !== null) {
            pushGTMEvent("purchase", result.data);
            // Stop futher submit stuff if redirecting
            if (result.data && result.data.RedirectURL)
                return;
            if (result.ok && result.data) {
                setFieldValue("__calculation.submitResult", result.data);
                submitForm();
            }
            else {
                setSubmitting(false);
                throw new Error("Failed to validate");
            }
        }
        else {
            submitForm();
        }
        if (!shouldValidate) {
            setTouched(touched);
            setErrors(errors);
        }
    }, [
        errors,
        fieldIsVisible,
        formHasErrors,
        onClick,
        pages,
        pushGTMEvent,
        sectionIsVisible,
        setErrors,
        setFieldValue,
        setSubmitting,
        setTouched,
        submitForm,
        touched
    ]);
    const navigation = useNavigationState();
    const roleHref = role === "navigate_before"
        ? getStepHref(step - 1)
        : role === "navigate_next"
            ? getStepHref(step + 1)
            : undefined;
    const submitWithValidation = useCallback(async (e) => submit(true, e), [submit]);
    const submitNoValidation = useCallback(async (e) => submit(false, e), [submit]);
    const roleClickHandler = useMemo(() => {
        if (!role)
            return undefined;
        const map = {
            navigate_before: e => {
                e.preventDefault();
                prev();
            },
            navigate_next: e => {
                e.preventDefault();
                navigateNext();
            },
            navigate_home: () => navigation.home(),
            submit: submitWithValidation,
            submit_no_validate: submitNoValidation,
            appActions: ({ currentTarget }) => setAppActionsAnchor(currentTarget),
            validateForm: validateForm
        };
        return map[role];
    }, [
        role,
        prev,
        navigateNext,
        navigation,
        submitWithValidation,
        submitNoValidation,
        validateForm
    ]);
    const labelIcon = useMemo(() => (icon && typeof icon === "string" ? _jsx(Icon, { className: icon }) : icon), [icon]);
    const defaultNavigateBeforeIcon = useMemo(() => _jsx(NavigateBefore, {}), []);
    const defaultNavigateNextIcon = useMemo(() => _jsx(NavigateNext, {}), []);
    const defaultSendIcon = useMemo(() => _jsx(Send, {}), []);
    const defaultMoreVertIcon = useMemo(() => _jsx(MoreVert, {}), []);
    const roleIcons = useMemo(() => {
        if (!role)
            return undefined;
        const map = {
            navigate_before: {
                startIcon: labelIcon ?? defaultNavigateBeforeIcon
            },
            navigate_next: {
                endIcon: labelIcon ?? defaultNavigateNextIcon
            },
            navigate_home: {},
            submit: {
                endIcon: labelIcon ?? defaultSendIcon
            },
            submit_no_validate: {
                endIcon: labelIcon ?? defaultSendIcon
            },
            appActions: {
                endIcon: labelIcon ?? defaultMoreVertIcon
            },
            validateForm: { endIcon: labelIcon ?? defaultSendIcon }
        };
        return map[role];
    }, [
        role,
        labelIcon,
        defaultNavigateBeforeIcon,
        defaultNavigateNextIcon,
        defaultSendIcon,
        defaultMoreVertIcon
    ]);
    const roleDisabled = useMemo(() => {
        if (!role)
            return undefined;
        const map = {
            navigate_before: step <= 0,
            navigate_next: isLastStep || hasPageErrors,
            navigate_home: !isLastStep,
            submit: disabled || hasFormErrors,
            submit_no_validate: disabled,
            appActions: !isLastStep,
            validateForm: hasFormErrors
        };
        return map[role];
    }, [role, step, isLastStep, hasPageErrors, disabled, hasFormErrors]);
    const iconProps = startIcon || endIcon
        ? {
            startIcon,
            endIcon
        }
        : roleIcons ?? {
            endIcon: labelIcon
        };
    const pendingSpinnerProps = usePendingSpinnerProps(pending, role, showOverlay);
    return (_jsxs(DployFormControl, { margin: margin, fullWidth: fullWidth, className: className, children: [pendingSpinnerProps.suspenseOverlay, _jsx(PendingButton, { style: style, component: roleHref ? "a" : undefined, href: roleHref, onClick: roleClickHandler || onClick, pending: pending, success: role === "submit" ? false : success, error: role === "submit" ? false : errorProps?.error, helperText: errorProps?.helperText, fullWidth: fullWidth, color: buttonColor, variant: buttonVariant, disabled: roleDisabled ?? disabled, size: buttonSize || "large", hideSpinner: pendingSpinnerProps.hideSpinnerIconFromButton, startIcon: (muiStartIcon && icons[muiStartIcon]) || iconProps.startIcon, endIcon: (muiEndIcon && icons[muiEndIcon]) || iconProps.endIcon, children: label }), role === "appActions" && isLastStep && (_jsx(AppActions, { form: form, anchor: appActionsAnchor, setAnchor: setAppActionsAnchor }))] }));
}
ButtonFieldImpl.displayName = "ButtonField";
export const ButtonField = memo(ButtonFieldImpl);
function ButtonLinkImpl(props) {
    const { label, color, variant, icon, field, form, onClick, fullWidth, disabled, options: { href, target, style }, errorDisplay, margin, modalText, startIcon, endIcon, mailto, pageToOpen, linkIsUrl, buttonSize, muiStartIcon, muiEndIcon } = props;
    const { value, name } = field;
    const { errors, touched } = form;
    const errorProps = getFieldError(errors, touched, field.name, errorDisplay);
    const error = getIn(form.errors, name);
    const pages = usePages();
    const { goto } = usePageState();
    const buttonColor = isButtonColor(color) ? color : undefined;
    const buttonVariant = isButtonVariant(variant) ? variant : undefined;
    const [success, setSuccess] = useState(false);
    const [responseError, setResponseError] = useState();
    const [modalOpen, setModalOpen] = useState(false);
    const classes = useStyles(props);
    const labelIcon = icon && typeof icon === "string" ? _jsx(Icon, { className: icon }) : icon;
    const onClickHandler = async (e) => {
        form.setFieldTouched(name, true);
        setResponseError(undefined);
        setSuccess(false);
        if (!onClick || error)
            return;
        try {
            const response = await onClick(e);
            if (response && response.data instanceof Blob) {
                download(response.data);
            }
            if (response && response.data && response.data.Error)
                setResponseError(response.data.Error);
            else
                setSuccess(true);
        }
        catch (e) {
            console.error(e);
        }
    };
    const button = mailto ? (value ? (_jsx(LiteralBaseInternal, { ...props, children: _jsx("a", { className: classes.mailtoButton, href: "mailto: " + value, children: value }) })) : (_jsx(_Fragment, {}))) : href || pageToOpen ? (_jsx(Button, { style: style, color: buttonColor, variant: buttonVariant, fullWidth: fullWidth, disabled: disabled || errorProps?.error, size: buttonSize || "large", component: "a", onClick: e => {
            if (!linkIsUrl) {
                if (pageToOpen) {
                    const index = pages
                        .map(p => (p.pageUri?.trim().length ?? 0) > 0 ? p.pageUri : p.pageTitle)
                        .indexOf(pageToOpen);
                    if (index !== -1)
                        goto(index);
                    else
                        goto(Number(pageToOpen));
                }
                else {
                    form.setFieldTouched(name, true);
                    if (error)
                        e.preventDefault();
                }
            }
            else {
                window.open(pageToOpen, "_blank");
            }
        }, href: href, target: target, startIcon: muiStartIcon && icons[muiStartIcon], endIcon: labelIcon || (muiEndIcon && icons[muiEndIcon]), children: label })) : (_jsx(PendingButton, { style: style, color: buttonColor, variant: buttonVariant, fullWidth: fullWidth, disabled: disabled || errorProps?.error, size: buttonSize || "large", onClick: async (e) => {
            if (modalText)
                setModalOpen(true);
            else
                await onClickHandler(e);
        }, success: false, startIcon: startIcon || (muiStartIcon && icons[muiStartIcon]), endIcon: (endIcon ?? labelIcon) || (muiEndIcon && icons[muiEndIcon]), children: label }));
    const helperText = (success && value) ||
        (errorProps?.error && errorProps.helperText) ||
        responseError;
    return (_jsxs(DployFormControl, { className: clsx({ [classes.buttonAsText]: buttonVariant === "text" }), error: errorProps?.error || Boolean(responseError), margin: mailto ? "none" : margin, fullWidth: fullWidth, children: [_jsx(Modal, { className: classes.modal, open: modalOpen, onClose: () => setModalOpen(false), children: _jsx(Paper, { className: classes.modalContainer, children: _jsxs("div", { className: classes.modalContent, children: [_jsx("div", { dangerouslySetInnerHTML: {
                                    __html: purifyMarked(modalText || "")
                                } }), _jsx(Button, { className: classes.modalConfirmButton, onClick: e => {
                                    setModalOpen(false);
                                    onClickHandler(e);
                                }, children: _jsx(FormattedMessage, { id: "ploy-ui.template-form.buttons.buttonlink.confirm", defaultMessage: "Aksepter", description: "Confirm button for download modal" }) })] }) }) }), button, helperText && (_jsx(Box, { component: FormHelperText, textAlign: "center", color: errorProps?.error || Boolean(responseError)
                    ? "error.main"
                    : success
                        ? "success.main"
                        : undefined, children: helperText }))] }));
}
ButtonLinkImpl.displayName = "ButtonLink";
export const ButtonLink = memo(ButtonLinkImpl);
const preparedButtonRoles = Object.entries(buttonRoleLocalization).map(([k, v]) => ({
    name: k,
    localization: v
}));
export const EditorButtonFields = identityRecordOfFieldEditorOptions({
    ButtonField: {
        fieldRoles: preparedButtonRoles,
        editableOptions: {
            variant: commonButtonVariants,
            color: commonButtonColors,
            buttonSize: ["small", "medium", "large"],
            muiStartIcons: Object.keys(icons),
            muiEndIcons: Object.keys(icons),
            eventCode: true
        }
    },
    ButtonLink: {
        fieldRoles: preparedButtonRoles,
        editableOptions: {
            variant: commonButtonVariants,
            color: commonButtonColors,
            linkIsUrl: true,
            mailto: true,
            modalText: true,
            pageToOpen: true,
            buttonSize: ["small", "medium", "large"],
            muiStartIcons: Object.keys(icons),
            muiEndIcons: Object.keys(icons)
        }
    }
});
const usePendingSpinnerProps = (pending, role, showOverlay) => {
    const displayPendingSuspenseSpinnerOverlay = (role === "submit" || showOverlay) && pending;
    return {
        hideSpinnerIconFromButton: displayPendingSuspenseSpinnerOverlay,
        suspenseOverlay: displayPendingSuspenseSpinnerOverlay && (_jsx(LoadingOverlay, { pending: pending }))
    };
};
//# sourceMappingURL=Button.js.map